import { useEffect, useState } from "react";
import { X, Save, Trash2 } from "lucide-react";
import { rgApi } from "@/lib/rgApi";

/**
 * ClosedPeriodModal — création/édition d'une période fermée volontaire.
 *
 * Props :
 *   open : bool
 *   onClose : () => void
 *   period : objet existant (édition) ou null (création)
 *   onSaved : () => void  — appelé après création/mise à jour/suppression
 */
const PRESET_REASONS = ["Travaux", "Personnel", "Maintenance", "Vacances", "Bloqué"];

export default function ClosedPeriodModal({ open, onClose, period, onSaved }) {
  const isEdit = !!period;
  const isLodgify = period?.source === "lodgify";
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [reason, setReason] = useState("Travaux");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      if (period) {
        setDateStart(period.date_start || "");
        setDateEnd(period.date_end || "");
        setReason(period.reason || "Travaux");
        setNote(period.note || "");
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setDateStart(today);
        setDateEnd(today);
        setReason("Travaux");
        setNote("");
      }
    }
  }, [open, period]);

  if (!open) return null;

  const save = async () => {
    if (!dateStart || !dateEnd || !reason.trim()) {
      setError("Renseigne les dates et un motif.");
      return;
    }
    if (dateEnd < dateStart) {
      setError("La date de fin doit être ≥ à la date de début.");
      return;
    }
    setBusy(true); setError(null);
    try {
      if (isEdit) {
        await rgApi.updateClosedPeriod(period.id, { date_start: dateStart, date_end: dateEnd, reason, note });
      } else {
        await rgApi.createClosedPeriod({ date_start: dateStart, date_end: dateEnd, reason, note });
      }
      if (onSaved) await onSaved();
    } catch (e) {
      setError(e.message || "Erreur lors de l'enregistrement.");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer cette période fermée ?")) return;
    setBusy(true); setError(null);
    try {
      await rgApi.deleteClosedPeriod(period.id);
      if (onSaved) await onSaved();
    } catch (e) {
      setError(e.message || "Erreur lors de la suppression.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="rg-modal-back" onClick={onClose} data-testid="rg-closed-back" />
      <div className="rg-modal" role="dialog" aria-modal="true" data-testid="rg-closed-modal">
        <div className="rg-modal-head">
          <div>
            <div className="rg-tiny">🔒 Période fermée{isLodgify ? " · 🔗 Source Lodgify" : ""}</div>
            <div className="rg-modal-title">{isEdit ? "Modifier" : "Nouvelle fermeture"}</div>
          </div>
          <button className="rg-icon-btn" onClick={onClose} data-testid="rg-closed-close"><X size={18} /></button>
        </div>

        <div className="rg-modal-body">
          {isLodgify && (
            <div className="rg-alert-warn" style={{ marginBottom: 12 }} data-testid="rg-closed-lodgify-banner">
              <b>Importée depuis Lodgify.</b> Cette fermeture est en lecture seule.
              Pour la modifier ou la supprimer, utilisez le dashboard Lodgify puis relancez la synchronisation.
            </div>
          )}
          <div className="rg-modal-section">
            <div className="rg-row" style={{ gap: 12, flexWrap: "wrap" }}>
              <div>
                <label className="rg-tiny">Du</label>
                <input
                  type="date"
                  className="rg-input"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  data-testid="rg-closed-start"
                  disabled={isLodgify}
                  style={{ display: "block", marginTop: 4 }}
                />
              </div>
              <div>
                <label className="rg-tiny">Au</label>
                <input
                  type="date"
                  className="rg-input"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  data-testid="rg-closed-end"
                  disabled={isLodgify}
                  style={{ display: "block", marginTop: 4 }}
                />
              </div>
            </div>
          </div>

          <div className="rg-modal-section">
            <label className="rg-tiny">Motif</label>
            <div className="rg-row" style={{ gap: 6, flexWrap: "wrap", marginTop: 4 }}>
              {PRESET_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`rg-btn ghost small ${reason === r ? "active" : ""}`}
                  onClick={() => setReason(r)}
                  data-testid={`rg-closed-preset-${r}`}
                >{r}</button>
              ))}
            </div>
            <input
              type="text"
              className="rg-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motif personnalisé…"
              maxLength={40}
              style={{ marginTop: 8, width: "100%" }}
              data-testid="rg-closed-reason"
            />
          </div>

          <div className="rg-modal-section">
            <label className="rg-tiny">Note interne (facultatif)</label>
            <textarea
              className="rg-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Ex : chantier peinture salon…"
              style={{ marginTop: 4, width: "100%", resize: "vertical", fontFamily: "inherit" }}
              data-testid="rg-closed-note"
            />
          </div>

          {error && <p className="rg-alert-warn" data-testid="rg-closed-error">{error}</p>}

          <div className="rg-row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap", justifyContent: "space-between" }}>
            {!isLodgify && (
              <button className="rg-btn" onClick={save} disabled={busy} data-testid="rg-closed-save">
                <Save size={13} /> {isEdit ? "Enregistrer" : "Créer"}
              </button>
            )}
            {isEdit && !isLodgify && (
              <button className="rg-btn ghost small" onClick={del} disabled={busy} data-testid="rg-closed-delete" style={{ color: "#dc2626" }}>
                <Trash2 size={13} /> Supprimer
              </button>
            )}
            {isLodgify && (
              <button className="rg-btn" onClick={onClose} data-testid="rg-closed-close-lodgify">
                Fermer
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
